'use client';
import { useEffect, useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { useRouter } from 'next/navigation';

export default function CheckoutPage() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    // Billing Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Payment Info
    cardNumber: '',
    expiry: '',
    cvv: '',
    // Billing Address
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Handle payment processing
    console.log('Checkout submitted:', formData);
    router.push('/thank-you');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const orderSummary = {
    items: [
      { name: 'Production Package - Professional', price: 15000 },
      { name: 'Brand Strategy Session', price: 2500 }
    ],
    subtotal: 17500,
    tax: 1443.75,
    total: 18943.75
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal">
              <SectionHeader
                eyebrow="Secure Checkout"
                title="Complete Your Order"
                description="Enter your payment information to finalize your purchase."
                align="center"
                className="mb-16"
              />
            </div>
          </div>
        </section>

        {/* Checkout Form */}
        <section className="py-24 px-6">
          <div className="max-w-[1200px] mx-auto">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                {/* Form Fields */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Billing Information */}
                  <div className="reveal">
                    <h3 className="text-2xl font-bold mb-6">Billing Information</h3>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          label="First Name"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="John"
                          required
                        />
                        <Input
                          label="Last Name"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Doe"
                          required
                        />
                      </div>
                      <Input
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                      />
                      <Input
                        label="Phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(702) 546-6337"
                        required
                      />
                    </div>
                  </div>

                  {/* Payment Information */}
                  <div className="reveal">
                    <h3 className="text-2xl font-bold mb-6">Payment Information</h3>
                    <div className="space-y-6">
                      <Input
                        label="Card Number"
                        name="cardNumber"
                        value={formData.cardNumber}
                        onChange={handleChange}
                        placeholder="1234 5678 9012 3456"
                        required
                      />
                      <div className="grid grid-cols-2 gap-6">
                        <Input
                          label="Expiry Date"
                          name="expiry"
                          value={formData.expiry}
                          onChange={handleChange}
                          placeholder="MM/YY"
                          required
                        />
                        <Input
                          label="CVV"
                          name="cvv"
                          value={formData.cvv}
                          onChange={handleChange}
                          placeholder="123"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Billing Address */}
                  <div className="reveal">
                    <h3 className="text-2xl font-bold mb-6">Billing Address</h3>
                    <div className="space-y-6">
                      <Input
                        label="Street Address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="123 Main St"
                        required
                      />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Input
                          label="City"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Las Vegas"
                          required
                        />
                        <Input
                          label="State"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          placeholder="NV"
                          required
                        />
                        <Input
                          label="ZIP Code"
                          name="zip"
                          value={formData.zip}
                          onChange={handleChange}
                          placeholder="89101"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                  <div className="reveal p-8 border border-border sticky top-24">
                    <h3 className="text-xl font-bold mb-6">Order Summary</h3>
                    <div className="space-y-4 mb-6">
                      {orderSummary.items.map((item, index) => (
                        <div key={index} className="pb-4 border-b border-border">
                          <p className="text-body-sm font-semibold mb-1">{item.name}</p>
                          <p className="text-body-sm text-muted-foreground">${item.price.toLocaleString()}</p>
                        </div>
                      ))}
                      <div className="flex justify-between pt-4">
                        <span className="text-body-sm text-muted-foreground">Subtotal</span>
                        <span className="text-body font-semibold">${orderSummary.subtotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-body-sm text-muted-foreground">Tax</span>
                        <span className="text-body font-semibold">${orderSummary.tax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="pt-4 border-t border-border">
                        <div className="flex justify-between">
                          <span className="text-body font-bold">Total</span>
                          <span className="text-2xl font-bold text-primary">${orderSummary.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                    <Button type="submit" variant="primary" size="lg" className="w-full">
                      Complete Purchase
                    </Button>
                    <p className="text-micro uppercase tracking-widest text-muted-foreground text-center mt-4">
                      Secure payment processing
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}